import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'horizontal' | 'compact';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  const { t } = useTranslation();

  if (variant === 'compact') {
    return (
      <article className="group">
        <Link to={`/article/${article.slug}`} className="block">
          <div className="flex gap-3">
            <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden bg-secondary-100">
              <img src={article.image} alt={article.title} className="w-full h-full object-cover" loading="lazy" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 mb-1">{article.title}</h3>
              <p className="text-xs text-foreground-500">{article.date} · {article.readTime}</p>
            </div>
          </div>
        </Link>
      </article>
    );
  }

  if (variant === 'horizontal') {
    return (
      <article className="group flex flex-col sm:flex-row gap-4 p-4 rounded-lg bg-background-50 border border-secondary-100 hover:border-primary-200 transition-all">
        <Link to={`/article/${article.slug}`} className="block flex-shrink-0 w-full sm:w-48 h-44 sm:h-32 rounded-lg overflow-hidden bg-secondary-100">
          <img src={article.image} alt={article.title} className="w-full h-full object-cover" loading="lazy" />
        </Link>
        <div className="flex-1 min-w-0 flex flex-col justify-between">
          <div>
            <Link to={`/article/${article.slug}`} className="block">
              <h3 className="text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 mb-2">{article.title}</h3>
            </Link>
            <p className="text-sm text-foreground-500 line-clamp-2">{article.excerpt}</p>
          </div>
          <div className="flex items-center gap-3 text-xs text-foreground-400 mt-3 flex-wrap">
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line"></i>
              {article.date}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {article.views.toLocaleString()} {t('views')}
            </span>
            <span>{article.readTime}</span>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="group rounded-xl overflow-hidden bg-background-50 border border-secondary-100 hover:border-primary-200 transition-all">
      <Link to={`/article/${article.slug}`} className="block">
        <div className="aspect-[16/10] overflow-hidden bg-secondary-100">
          <img src={article.image} alt={article.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
        </div>
      </Link>
      <div className="p-5">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          {article.tags.slice(0, 2).map((tag) => (
            <span key={tag} className="px-2 py-0.5 text-xs rounded-full bg-accent-100 text-accent-700">{tag}</span>
          ))}
        </div>
        <Link to={`/article/${article.slug}`} className="block">
          <h3 className="text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 mb-2">{article.title}</h3>
        </Link>
        <p className="text-sm text-foreground-500 line-clamp-2 mb-4">{article.excerpt}</p>
        <div className="flex items-center justify-between text-xs text-foreground-400">
          <span className="flex items-center gap-1">
            <i className="ri-calendar-line"></i>
            {article.date}
          </span>
          <span>{article.readTime}</span>
        </div>
      </div>
    </article>
  );
}